// ---------------------------------------------------------------------------
// Thin fetch wrapper.
//
// Requests go to a relative /api path, which Vite proxies to the Express server
// in development and a reverse proxy serves in production — so no API host is
// ever baked into the bundle.
// ---------------------------------------------------------------------------

const BASE = import.meta.env.VITE_API_URL ?? '/api';

export class ApiClientError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let accessToken = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

async function request(path, { method = 'GET', body, headers = {}, signal, raw } = {}) {
  const init = { method, headers: { ...headers }, signal };

  if (body !== undefined) {
    if (raw) {
      init.body = body; // FormData sets its own multipart boundary.
    } else {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
  }

  if (accessToken) init.headers.Authorization = `Bearer ${accessToken}`;

  let response;
  try {
    response = await fetch(`${BASE}${path}`, init);
  } catch (cause) {
    throw new ApiClientError(0, 'NETWORK_ERROR', 'Cannot reach the server.', { cause: String(cause) });
  }

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = payload?.error ?? {};
    throw new ApiClientError(
      response.status,
      error.code ?? 'UNKNOWN_ERROR',
      error.message ?? response.statusText,
      error.details,
    );
  }

  return payload;
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
  upload: (path, formData) => request(path, { method: 'POST', body: formData, raw: true }),
};
